import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Link as RouterLink, RouteComponentProps } from 'react-router';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import {
  Button,
  Container,
  Grid,
  Icon,
  Link,
  PageLoader,
  Typography as ExtendTypography
} from '../../../ui';
import { basketActions as basketThunkActions } from '../../actions';
import * as Api from '../../Api';
import { basketActions, basketSelectors, paymentActions } from '../../redux';
import { orderApiServices, storageServices } from '../../services';
import * as Store from '../../Store';
import CountDown from './components/CountDown';
import Paper from './components/Paper';
import { MainLayout } from '../MainLayout/MainLayout';

const ORDER_CANCELLED_STATES = [
  Api.OrderState.Cancelled,
  Api.OrderState.RejectedByRestaurant,
  Api.OrderState.RejectedByFlipdish,
  Api.OrderState.RejectedAutomatically,
  Api.OrderState.RejectedAfterBeingAccepted
];
const ORDER_ACCEPTED_STATES = [
  Api.OrderState.AcceptedByRestaurant,
  Api.OrderState.Delivered,
  Api.OrderState.Dispatched,
  Api.OrderState.PlacedCanBeCancelled
];

interface RouterParams
  extends RouteComponentProps<
    {
      orderId: string;
      concessionStoreId?: string;
    },
    {}
  > {}

type Props = RouterParams &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const Typography = ExtendTypography as any;

export const OrderView = (props: Props) => {
  const {
    params,
    translate,
    ClearBasket,
    cancelTimeout,
    continueCheckoutFlow,
    cancelCheckoutFlow,
    setDefaultPayment
  } = props;
  const { orderId, concessionStoreId } = params;

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<Api.PreviousOrderDm & { isCancelled: boolean }>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (orderId) {
      setLoading(true);

      orderApiServices
        .get({ orderId })
        .then((o) => {
          const orderCancelled = ORDER_CANCELLED_STATES.includes(o.OrderState);

          // If accepted order from redirect clear basket
          // eslint-disable react-hooks/exhaustive-deps
          if (!cancelTimeout && !orderCancelled) {
            const meta = storageServices.getSubmittedOrderMeta(orderId);
            const menuId = (meta && meta.menuId) || undefined;
            const paymentId = meta && meta.paymentId;

            if (paymentId) {
              setDefaultPayment({ paymentId });
            }

            // eslint-disable react-hooks/exhaustive-deps
            ClearBasket({
              menuId
            });
          }

          setOrder({ ...o, isCancelled: orderCancelled });
          setError(undefined);
          setLoading(false);
        })
        .catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error(error);
          }
          setError(new Error('Order_loading_error'));
          setLoading(false);
        });
    }
  }, [orderId]);

  //#region CONTINUE CHECKOUT FLOW ON NAVIGATE AWAY IF NOT CANCEL
  const isCancelFlag = useRef(false);
  useEffect(() => {
    return () => {
      if (!isCancelFlag.current) {
        continueCheckoutFlow();
      }
    };
  }, []);
  //#endregion

  const renderMenuLink = useMemo(() => {
    let menuRoute = '/';
    if (order) {
      const vrId = order.VirtualRestaurantId;
      if (order.DeliveryType === Api.DeliveryType.Collection) {
        menuRoute = `/restaurant/${vrId}/collection/${order.PhysicalRestaurantId}`;
      } else {
        menuRoute = `/restaurant/${vrId}/delivery/${order.DeliveryLocationId}`;
      }
      if (concessionStoreId) {
        menuRoute = menuRoute + `/${concessionStoreId}`;
      }
    }

    return React.forwardRef((props, ref) => (
      <RouterLink ref={ref as any} {...props} to={menuRoute} />
    ));
  }, [order, concessionStoreId]);

  // here cancelCheckoutFlow will cancel order
  const handleCancelOrder = () => {
    isCancelFlag.current = true;
    cancelCheckoutFlow();
  };

  if (error) {
    return (
      <Container maxWidth="md">
        <Paper square>
          <Grid container justify="center" alignItems="center">
            <Grid item>
              <Typography variant="h4" color="error">
                {translate(error.message, undefined, {
                  missingTranslationMsg: error.message
                })}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    );
  }

  if (loading) {
    return <PageLoader fullScreen />;
  }

  return (
    <MainLayout>
      <Container maxWidth="md">
        <Paper square>
          <Grid container spacing={3} direction="column" justify="center" alignItems="center">
            <Grid item xs={12}>
              <Typography color="primary" variant="h1" align="center" display="block" gutterBottom>
                <Typography
                  color="primary"
                  display="block"
                  variant="h1"
                  component="span"
                  gutterBottom
                >
                  {order.isCancelled ? translate('Cancelled') : translate('Thank_you')}
                </Typography>

                <Icon
                  color="primary"
                  // TODO 65px
                  fontSize="large"
                  className={`fa ${order.isCancelled ? 'fa-times-circle' : 'fa-check-circle'}`}
                  aria-hidden="true"
                />

                <Typography
                  color="primary"
                  display="block"
                  variant="h1"
                  component="span"
                  gutterBottom
                >
                  {order.isCancelled
                    ? translate('This_order_has_been_cancelled')
                    : translate('Your_order_has_been_placed')}
                </Typography>

                <Typography color="textSecondary" display="block" variant="h1" component="span">
                  {order.RestaurantName}
                </Typography>
              </Typography>
            </Grid>

            {order.DeliveryType === Api.DeliveryType.Delivery ? (
              <Grid item xs={12}>
                <Typography variant="h2" color="textPrimary">
                  {translate('Delivering_to_order', {
                    address: order.DeliveryLocationAddressString
                  })}
                </Typography>
              </Grid>
            ) : null}

            {cancelTimeout > 0 ? (
              <Grid item xs={12}>
                <CountDown
                  text={translate('Cancel_order') as string}
                  timeout={cancelTimeout}
                  onCancel={handleCancelOrder}
                />
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <Button
                component={renderMenuLink}
                size="large"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {translate('Back_to_menu')}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Link
                color="textSecondary"
                underline="always"
                href="https://www.flipdish.com/"
                target="_blank"
              >
                {translate('Powered_by_flipdish')}
              </Link>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </MainLayout>
  );
};

const mapStateToProps = (s: Store.State, props: RouterParams) => {
  return {
    cancelTimeout: basketSelectors.getSubmittedOrderCancelTimeout(s),
    translate: getTranslate(s.locale)
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<Store.State, {}, Action>) => {
  return {
    continueCheckoutFlow: () => {
      dispatch(basketActions.continueCheckoutFlow());
    },
    cancelCheckoutFlow: () => {
      dispatch(basketActions.cancelCheckoutFlow());
    },
    setDefaultPayment: (payload: { paymentId: string }) => {
      dispatch(paymentActions.setDefaultSelected(payload));
    },

    ClearBasket: (payload: basketThunkActions.ClearBasketProps) => {
      dispatch(basketThunkActions.clearBasket(payload));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderView);
